import { FC, useEffect, useState } from "react";
import "./ModalAccessPageInsert.scss";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Card, Col, FormCheck, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useAccessPage } from "../../../../providers/Access/AccessPage/AccessPageProvider";
import { AccessPageModel } from "../../../../models/Access/AccessPage/AccessPageModel";
import ModalOffCanvas from "../../../ModalOffCanvas/ModalOffCanvas";
import IconGallery from "../../../IconGallery/IconGallery";
import { BvIcon } from "bevi-icon";
import SkeletonTable from "../../../SkeletonTable/SkeletonTable";
import ModalAccessPageCollapseInsert from "../../../ModalAccessPageCollapseInsert/ModalAccessPageCollapseInsert";
import { JSX } from "react/jsx-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { icons } from "../../../../enums/Icons";

interface ModalAccessPageInsertProps {
  show: boolean;
  onClose: () => void;
}

type FormValues = {
  id?: number;
  name?: string;
  icon?: string;
  collapse?: boolean;
};

type Icon = {
  icon: string;
  name: string;
};

type NewPage = {
  id?: number;
  name: string;
  description: string;
  layout: string;
  icon: string;
  collapse: boolean;
};

const defaultValue = {
  name: "",
  icon: "",
  collapse: false,
  layout: "",
  description: "",
};

const ModalAccessPageInsert: FC<ModalAccessPageInsertProps> = ({
  show,
  onClose,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [isCollapse, setIsCollapse] = useState<boolean>(false);
  const [showModalOffCanvas, setShowModalOffCanvas] = useState<boolean>(false);
  const [showModalAccessPageCollapse, setShowModalAccessPageCollapse] =
    useState<boolean>(false);
  const [iconsFiltered, setIconsFiltered] = useState<Icon>();
  const [iconEdit, setIconEdit] = useState<Icon[]>([]);
  const [newPagesRegistered, setNewPagesRegistered] = useState<NewPage[]>([]);
  const [isLoadingNewPage, setIsLoadingNewPage] = useState<boolean>(false);
  const [pageEdit, setPageEdit] = useState<NewPage>();
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const {
    isLoading,
    accessPage,
    pagesRegistration,
    setAccessPage,
    setError,
    handleList,
    handleFetch,
    handleDelete,
    handleGet,
  } = useAccessPage();
  const { onInvalid } = useAuth();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const newProcess = () => {
    setValue("id", 0);
    setValue("name", "");
    setValue("description", "");
    setValue("icon", "");
    setValue("layout", "");
    setValue("path_name", "");
    setValue("collapse", false);
    setValue("state", "");
    setValue("alias", "");
    setValue("order", 0);

    reset(new AccessPageModel());
    setAccessPage({} as AccessPageModel);
    setIconsFiltered({ icon: "", name: "" });
    setError("");
    setIconEdit([]);
    setNewPagesRegistered([]);
    setIsCollapse(false);
  };

  const handleClose = () => {
    newProcess();
    onClose();
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError("");

    const ret = await handleFetch(data as AccessPageModel);

    if (ret.success) {
      if (newPagesRegistered.length > 0) {
        newPagesRegistered.forEach((element: NewPage, index: number) => {
          const page = {
            id: element.id ?? undefined,
            name: element.name,
            description: element.description,
            icon: element.icon,
            path_name: null,
            layout: element.layout,
            collapse: element.collapse,
            state: null,
            order: index,
            alias: element.layout,
            parent_id: ret.access,
          };

          handleFetch(page);
        });
        toast.success("Página salva com sucesso!");
        handleList();
        onClose();
      } else {
        toast.success("Página salva com sucesso!");
        handleList();
        onClose();
      }
    }

    setIsLoadingSubmit(false);
  };

  useEffect(() => {
    if (show) {
      if (!!accessPage?.id) {
        handleGet({ filter: { name: accessPage.name } });
        setIconsFiltered({ icon: "", name: "" });
      } else {
        newProcess();
      }
    }
  }, [show]);

  useEffect(() => {
    if (pagesRegistration?.id && pagesRegistration?.id > 0) {
      reset(pagesRegistration as FormValues);
      setIconEdit(
        pagesRegistration?.icon
          ? icons.items.filter((item) => item.icon === pagesRegistration?.icon)
          : []
      );
      setIsCollapse(pagesRegistration?.collapse ? true : false);
      if (
        pagesRegistration?.children &&
        pagesRegistration?.children?.length > 0
      ) {
        const pages = pagesRegistration?.children.map(
          (item: AccessPageModel) => ({
            id: item.id ?? 0,
            name: item.name ?? "",
            icon: item.icon ?? "",
            collapse: item.collapse ?? false,
            layout: item.layout ?? "",
            description: item.description ?? "",
          })
        );
        setNewPagesRegistered(pages);
      }
    }
  }, [pagesRegistration]);

  const changeIsCollapse = (action: boolean) => {
    setAccessPage({
      ...accessPage,
      collapse: action,
    });
    setIsCollapse(action);
  };

  const handleSelect = (icon: string, name: string) => {
    setIconsFiltered({ icon, name });
    setValue("icon", icon);
    setAccessPage({
      ...accessPage,
      icon: icon,
    });
    setShowModalOffCanvas(false);
  };

  const handlePagesAdd = (page: NewPage, update: boolean) => {
    if (page.name.length === 0) return;

    setIsLoadingNewPage(true);

    setNewPagesRegistered((prevPages) => {
      if (update) {
        return prevPages.map((item) =>
          item.id === page.id ? { ...item, ...page } : item
        );
      } else {
        return [...prevPages, page];
      }
    });

    setTimeout(() => {
      setIsLoadingNewPage(false);
      toast.success(
        update
          ? "Página atualizada com sucesso!"
          : "Página inserida com sucesso!"
      );
    }, 500);
  };

  const showOffCanvas = (show: boolean) => {
    setShowModalOffCanvas(show);
  };

  const handleShowModalCollapse = () => {
    setShowModalAccessPageCollapse(true);
  };

  const handleEdit = (page: NewPage) => {
    setShowModalAccessPageCollapse(true);
    setPageEdit(page);
    setIsEdit(true);
  };

  const handleCloseCollapse = () => {
    setShowModalAccessPageCollapse(false);
    setPageEdit(defaultValue);
    setIsEdit(false);
  };

  const handleDeletePage = async (item: NewPage) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja remover a página #${item.name} ?</span>`,
      icon: "question",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Não",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x) => {
      if (x.isConfirmed) {
        setIsLoadingNewPage(true);

        if (item.id && item.id > 0) {
          const del = await handleDelete(item.id);

          if (del) {
            toast.success("Página removida com sucesso!");
            setTimeout(() => {
              setIsLoadingNewPage(false);
              setNewPagesRegistered((prev) =>
                prev.filter((x) => x.id !== item.id)
              );
            }, 500);
          }
        } else {
          setTimeout(() => {
            setIsLoadingNewPage(false);
            setNewPagesRegistered((prev) =>
              prev.filter((item) => item.name !== item.name)
            );
          }, 500);
        }

        setIsLoadingNewPage(false);
      }
    });
  };

  const renderList = (): JSX.Element => {
    return (
      <>
        {newPagesRegistered?.length > 0 ? (
          <>
            {newPagesRegistered.map((x: NewPage, key: number) => (
              <tr key={key} className="text-sm">
                <td>{x?.name}</td>
                <td>{x?.layout}</td>
                <td className="text-center">
                  <FontAwesomeIcon
                    onClick={() => handleEdit(x)}
                    icon={faEdit}
                    className="me-2 cursor"
                  />
                  <FontAwesomeIcon
                    onClick={() => handleDeletePage(x)}
                    icon={faTrash}
                    className="cursor"
                  />
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={8} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderTable = (): JSX.Element => {
    return (
      <>
        {!isLoadingNewPage ? (
          <div className="table-responsive">
            <table className="table table-flush m-0 w-100">
              <thead className="thead-light">
                <tr>
                  <th className="text-uppercase opacity-7" scope="col">
                    NOME
                  </th>
                  <th className="text-uppercase opacity-7" scope="col">
                    URL
                  </th>
                  <th
                    className="text-uppercase opacity-7 text-center"
                    scope="col"
                  >
                    AÇÃO
                  </th>
                </tr>
              </thead>
              <tbody>{renderList()}</tbody>
            </table>
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  const renderCollapsePag = () => {
    return (
      <Card>
        <Card.Header>
          <Row>
            <Col md={6} sm={12}>
              <h5>Páginas</h5>
            </Col>
            <Col
              className="d-flex justify-content-sm-end justify-content-start"
              md={6}
              sm={12}
            >
              <button
                onClick={() => handleShowModalCollapse()}
                className="btn btn-primary btn-xs m-0 btn btn-primary"
              >
                Adicionar Página
              </button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>{renderTable()}</Card.Body>
      </Card>
    );
  };

  const renderContent = () => {
    return (
      <Row>
        <Col md={12} lg={7}>
          <Row>
            <Col md={12}>
              <FormGroup className="form-group">
                <label htmlFor="name">NOME *</label>
                <input
                  className={`form-control ${onInvalid(errors?.name)}`}
                  type="text"
                  {...register("name")}
                  onChange={(e: any) =>
                    setAccessPage({ ...accessPage, name: e?.target?.value })
                  }
                />
                <ErrorMessage
                  name={"Nome"}
                  type={errors?.name?.type?.toString()}
                  min={3}
                  max={50}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <FormGroup className="form-group">
                <label htmlFor="description">DESCRIÇÃO</label>
                <textarea
                  className={`form-control ${onInvalid(errors?.description)}`}
                  rows={3}
                  {...register("description")}
                  onChange={(e: any) =>
                    setAccessPage({
                      ...accessPage,
                      description: e?.target?.value,
                    })
                  }
                ></textarea>
                <ErrorMessage
                  name={"Descrição"}
                  type={errors?.description?.type?.toString()}
                  max={255}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col sm={6} md={4}>
              <FormGroup className="form-group">
                <label htmlFor="path_name">URL *</label>
                <div className={`input-group ${onInvalid(errors?.path_name)}`}>
                  <span
                    style={{ paddingRight: 7 }}
                    className="input-group-text"
                  >
                    /
                  </span>
                  <input
                    placeholder=" Ex: /registrations"
                    className="form-control"
                    type="text"
                    {...register("path_name")}
                    onChange={(e: any) => {
                      setAccessPage({
                        ...accessPage,
                        state: e?.target?.value + "Collapse",
                        alias: e?.target?.value,
                        order: 0,
                      });
                      setValue("alias", e?.target?.value);
                      setValue("state", e?.target?.value + "Collapse");
                      setValue("order", 0);
                    }}
                  />
                </div>
                <ErrorMessage
                  name={"URL"}
                  type={errors?.path_name?.type?.toString()}
                  min={3}
                  max={50}
                />
              </FormGroup>
            </Col>
            <Col sm={6} md={4}>
              <FormGroup className="form-group">
                <label>ÍCONE</label>
                <div className="col-auto">
                  <div className="input-group mb-3">
                    {iconsFiltered?.icon ? (
                      <div className="input-group-append d-flex align-items-center iconFilter">
                        <BvIcon
                          name={iconsFiltered.icon}
                          variant="solid"
                          className="mx-1"
                          width={30}
                          height={30}
                        />
                      </div>
                    ) : iconEdit.length > 0 ? (
                      <div className="input-group-append d-flex align-items-center iconFilter">
                        <BvIcon
                          name={iconEdit?.[0]?.icon}
                          variant="solid"
                          className="mx-1"
                          width={30}
                          height={30}
                        />
                      </div>
                    ) : null}
                    <input
                      type="text"
                      className="form-control"
                      disabled={true}
                      value={
                        iconsFiltered?.name !== ""
                          ? iconsFiltered?.name
                          : iconEdit?.[0]?.name
                      }
                    />
                    <div className="input-group-append d-flex align-items-center iconSearch">
                      <a onClick={() => showOffCanvas(true)}>
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </FormGroup>
            </Col>
            <Col sm={6} md={4}>
              <FormGroup className="form-group">
                <label htmlFor="collapse">COLLAPSE *</label>
                <br />
                <FormCheck className="form-check" inline>
                  <input
                    className={`form-check-input ${onInvalid(
                      errors?.collapse
                    )}`}
                    type="radio"
                    id="cS"
                    value="true"
                    {...register("collapse")}
                    onChange={(e) => {
                      changeIsCollapse(true);
                    }}
                    checked={accessPage.collapse}
                  />
                  <label className="form-check-label" htmlFor="cS">
                    Sim
                  </label>
                </FormCheck>
                <FormCheck className="form-check" inline>
                  <input
                    className={`form-check-input ${onInvalid(
                      errors?.collapse
                    )}`}
                    type="radio"
                    id="cN"
                    value="false"
                    {...register("collapse")}
                    onChange={(e) => {
                      changeIsCollapse(false);
                    }}
                    checked={!accessPage.collapse}
                  />
                  <label className="form-check-label" htmlFor="cN">
                    Não
                  </label>
                </FormCheck>
                <ErrorMessage
                  name={"Collapse"}
                  type={errors?.collapse?.type?.toString()}
                />
              </FormGroup>
            </Col>
            {isCollapse ? renderCollapsePag() : null}
          </Row>
        </Col>
        <Col md={12} lg={5}>
          <Card className="h-100">
            <Card.Header>
              <h5>Prévia do Menu</h5>
            </Card.Header>
            <Card.Body className="pt-0">
              <div className="d-flex align-items-center">
                {iconsFiltered?.icon ? (
                  <BvIcon
                    name={iconsFiltered?.icon}
                    weight={600}
                    variant="solid"
                    className="me-2"
                    width={30}
                    height={30}
                  />
                ) : iconEdit.length > 0 ? (
                  <BvIcon
                    name={iconEdit?.[0]?.icon}
                    weight={600}
                    variant="solid"
                    className="me-2"
                    width={30}
                    height={30}
                  />
                ) : null}
                {accessPage?.name}
              </div>
              <div>
                {newPagesRegistered.length > 0 ? (
                  <div>
                    {newPagesRegistered?.map((item: NewPage) => (
                      <div className="d-flex align-items-center mt-3 mb-2 ms-4">
                        {item.icon && (
                          <BvIcon
                            name={item.icon ?? ""}
                            variant="solid"
                            weight={600}
                            width={25}
                            height={25}
                            className="me-2"
                          />
                        )}
                        {item.name}
                      </div>
                    ))}
                  </div>
                ) : accessPage?.children && accessPage?.children?.length > 0 ? (
                  <div>
                    {accessPage?.children?.map((item: AccessPageModel) => (
                      <div className="d-flex align-items-center mt-3 mb-2">
                        <BvIcon
                          name={item.icon ?? ""}
                          variant="solid"
                          weight={600}
                          width={25}
                          height={25}
                          className="ms-4 me-2"
                        />
                        {item.name}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <ModalDefault
      title={
        !!accessPage?.id
          ? "Editar Página de Acesso"
          : "Adicionar Página de Acesso"
      }
      icon="article"
      show={show}
      onClose={handleClose}
      sizeModal={"xl"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalAccessPageInsert"
        data-testid="ModalAccessPageInsert"
      >
        {renderContent()}
        <ModalOffCanvas
          title="Bevi Icon"
          show={showModalOffCanvas}
          body={<IconGallery handleSelect={handleSelect} />}
          onClose={() => setShowModalOffCanvas(false)}
        />
        <ModalAccessPageCollapseInsert
          show={showModalAccessPageCollapse}
          onClose={() => handleCloseCollapse()}
          handlePagesAdd={handlePagesAdd}
          pageEdit={pageEdit}
          isEdit={isEdit}
        />
      </div>
    </ModalDefault>
  );
};

export default ModalAccessPageInsert;
